// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// Teeter — the deterministic marble simulation. A ball rolls in continuous grid-unit
// space under a tilt-derived gravity vector, colliding with maze walls, falling into
// pits, collecting gems, and reaching the exit. NO Math.random in here and a FIXED
// substep, so (same maze, same tilt sequence) is byte-identical on every machine —
// which is what makes the sim a fair referee and the same-seed race fair by
// construction. The speed cap is set below the wall thickness so the ball can never
// tunnel through a wall in a single substep (asserted in tests/physics).

import { buildMaze, cellCenter, solidAt, type Cell, type Maze } from './maze';
import type { Mode } from './modes';

/* ── tunables (one place; the balance sim refereed these) ─────────────────── */
export const SUB_DT = 1 / 120; // fixed physics substep, seconds
export const BALL_R = 0.34; // marble radius, grid units (corridor is 1 wide)
const ACCEL = 18; // gravity accel per unit tilt, units/s^2
const FRICTION_PER_SEC = 0.1; // fraction of velocity kept after 1s at zero tilt
const MAX_V = 9; // speed cap, units/s — MAX_V*SUB_DT (0.075) << BALL_R, so no tunneling
const RESTITUTION = 0.18; // wall bounce
const BUMP_SPEED = 2.2; // min impact speed that registers a bump (sound/shake)
const HOLE_R = 0.4; // fall zone radius around a pit centre
const GEM_R = 0.55; // gem pickup radius
const FRICTION_SUB = Math.pow(FRICTION_PER_SEC, SUB_DT); // per-substep retention

export type GameEvent =
  | { t: 'gem'; i: number }
  | { t: 'fall' }
  | { t: 'respawn' }
  | { t: 'bump'; speed: number }
  | { t: 'win' }
  | { t: 'timeout' };

export interface Ball {
  x: number;
  y: number;
  vx: number;
  vy: number;
}

export interface GameState {
  mode: Mode;
  seed: number;
  maze: Maze;
  ball: Ball;
  /** collected[k] === true once gem k is taken. */
  collected: boolean[];
  gemsLeft: number;
  falls: number;
  elapsedMs: number;
  over: boolean;
  finished: boolean; // reached the exit
  dnf: boolean; // ran out of time
  reason: 'win' | 'timeout' | null;
  events: GameEvent[];
  acc: number; // substep time accumulator, ms
}

export function createGame(mode: Mode, seed: number): GameState {
  const maze = buildMaze(mode, seed);
  const c = cellCenter(maze.start);
  return {
    mode,
    seed,
    maze,
    ball: { x: c.x, y: c.y, vx: 0, vy: 0 },
    collected: maze.gems.map(() => false),
    gemsLeft: maze.gems.length,
    falls: 0,
    elapsedMs: 0,
    over: false,
    finished: false,
    dnf: false,
    reason: null,
    events: [],
    acc: 0,
  };
}

/** Drain accumulated events (for sound/particles or the audit), clearing the buffer. */
export function drainEvents(s: GameState): GameEvent[] {
  if (s.events.length === 0) return [];
  const out = s.events;
  s.events = [];
  return out;
}

const dist2 = (ax: number, ay: number, bx: number, by: number): number =>
  (ax - bx) * (ax - bx) + (ay - by) * (ay - by);

/** Push the ball out of any solid cell it overlaps; kill the normal velocity. */
function resolveCollisions(s: GameState): void {
  const b = s.ball;
  for (let pass = 0; pass < 3; pass++) {
    const cgx = Math.floor(b.x);
    const cgy = Math.floor(b.y);
    let hit = false;
    for (let gy = cgy - 1; gy <= cgy + 1; gy++) {
      for (let gx = cgx - 1; gx <= cgx + 1; gx++) {
        if (!solidAt(s.maze, gx, gy)) continue;
        // Closest point on the cell AABB [gx,gx+1]x[gy,gy+1] to the ball centre.
        const px = b.x < gx ? gx : b.x > gx + 1 ? gx + 1 : b.x;
        const py = b.y < gy ? gy : b.y > gy + 1 ? gy + 1 : b.y;
        let dx = b.x - px;
        let dy = b.y - py;
        let d = Math.hypot(dx, dy);
        if (d >= BALL_R) continue;
        if (d < 1e-6) {
          // Centre sits on the edge/inside — push toward the cell's outward normal.
          dx = b.x - (gx + 0.5);
          dy = b.y - (gy + 0.5);
          d = Math.hypot(dx, dy) || 1;
          if (Math.abs(dx) > Math.abs(dy)) {
            dx = Math.sign(dx) || 1;
            dy = 0;
          } else {
            dx = 0;
            dy = Math.sign(dy) || 1;
          }
          d = 1;
        }
        const nx = dx / d;
        const ny = dy / d;
        const pen = BALL_R - d;
        b.x += nx * pen;
        b.y += ny * pen;
        const vn = b.vx * nx + b.vy * ny;
        if (vn < 0) {
          if (-vn > BUMP_SPEED) s.events.push({ t: 'bump', speed: -vn });
          b.vx -= (1 + RESTITUTION) * vn * nx;
          b.vy -= (1 + RESTITUTION) * vn * ny;
        }
        hit = true;
      }
    }
    if (!hit) break;
  }
}

function checkHazards(s: GameState): void {
  const b = s.ball;
  // Pit: fall and respawn at the start.
  for (const h of s.maze.holes) {
    const c = cellCenter(h);
    if (dist2(b.x, b.y, c.x, c.y) < HOLE_R * HOLE_R) {
      s.falls++;
      s.events.push({ t: 'fall' });
      const start = cellCenter(s.maze.start);
      b.x = start.x;
      b.y = start.y;
      b.vx = 0;
      b.vy = 0;
      s.events.push({ t: 'respawn' });
      return;
    }
  }
  // Gems.
  for (let i = 0; i < s.maze.gems.length; i++) {
    if (s.collected[i]) continue;
    const c = cellCenter(s.maze.gems[i]);
    if (dist2(b.x, b.y, c.x, c.y) < GEM_R * GEM_R) {
      s.collected[i] = true;
      s.gemsLeft--;
      s.events.push({ t: 'gem', i });
    }
  }
  // Exit (only opens once every gem is collected).
  if (s.gemsLeft === 0) {
    if (Math.floor(b.x) === s.maze.exit.gx && Math.floor(b.y) === s.maze.exit.gy) {
      s.finished = true;
      s.over = true;
      s.reason = 'win';
      s.events.push({ t: 'win' });
    }
  }
}

/** One fixed-dt physics substep under gravity vector `tilt` (each component -1..1). */
export function substep(s: GameState, tilt: { x: number; y: number }): void {
  if (s.over) return;
  const b = s.ball;
  b.vx += tilt.x * ACCEL * SUB_DT;
  b.vy += tilt.y * ACCEL * SUB_DT;
  b.vx *= FRICTION_SUB;
  b.vy *= FRICTION_SUB;
  const sp = Math.hypot(b.vx, b.vy);
  if (sp > MAX_V) {
    b.vx = (b.vx / sp) * MAX_V;
    b.vy = (b.vy / sp) * MAX_V;
  }
  b.x += b.vx * SUB_DT;
  b.y += b.vy * SUB_DT;
  resolveCollisions(s);
  checkHazards(s);
  s.elapsedMs += SUB_DT * 1000;
  if (!s.over && s.elapsedMs >= s.mode.limitMs) {
    s.over = true;
    s.dnf = true;
    s.reason = 'timeout';
    s.events.push({ t: 'timeout' });
  }
}

/** Advance the sim by `dtMs` of wall-clock time in fixed substeps (rAF-friendly). */
export function step(s: GameState, tilt: { x: number; y: number }, dtMs: number): void {
  if (s.over) return;
  s.acc += Math.min(dtMs, 100); // clamp a huge tab-refocus jump
  const stepMs = SUB_DT * 1000;
  let guard = 0;
  while (s.acc >= stepMs && !s.over && guard < 1000) {
    substep(s, tilt);
    s.acc -= stepMs;
    guard++;
  }
}

/** Exposed for the mechanism audit / renderer. */
export const HAZARD = { HOLE_R, GEM_R, BALL_R, MAX_V, SUB_DT } as const;

/** True if world point (x,y) sits inside the exit cell. */
export function atExit(maze: Maze, x: number, y: number): boolean {
  return Math.floor(x) === maze.exit.gx && Math.floor(y) === maze.exit.gy;
}

export type { Cell };
